import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

export default function AdminCustomers() {
  const { token } = useAuthStore();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/admin/customers', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      } else {
        setError('Failed to load customers');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    try {
      const response = await fetch(`http://localhost:3001/api/admin/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedCustomer(data);
      } else {
        setError('Failed to load customer details');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading customers...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <a href="/admin" className="text-blue-600 hover:text-blue-800">
            ← Back to Admin
          </a>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">Customers</h1>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {selectedCustomer ? (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="mb-4">
              <button
                onClick={() => setSelectedCustomer(null)}
                className="text-blue-600 hover:text-blue-800"
              >
                ← Back to Customers
              </button>
            </div>

            <h2 className="text-2xl font-bold mb-6">
              {selectedCustomer.firstName} {selectedCustomer.lastName}
            </h2>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-gray-600 text-sm">Email</label>
                <div className="text-lg">{selectedCustomer.email}</div>
              </div>
              <div>
                <label className="text-gray-600 text-sm">Status</label>
                <div
                  className={`text-lg font-semibold ${
                    selectedCustomer.status === 'active'
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {selectedCustomer.status?.toUpperCase()}
                </div>
              </div>
              <div>
                <label className="text-gray-600 text-sm">Company</label>
                <div className="text-lg">{selectedCustomer.company || 'N/A'}</div>
              </div>
              <div>
                <label className="text-gray-600 text-sm">Country</label>
                <div className="text-lg">{selectedCustomer.country || 'N/A'}</div>
              </div>
              <div>
                <label className="text-gray-600 text-sm">Account Balance</label>
                <div className="text-lg font-semibold">
                  ${parseFloat(selectedCustomer.accountBalance).toFixed(2)}
                </div>
              </div>
              <div>
                <label className="text-gray-600 text-sm">Member Since</label>
                <div className="text-lg">
                  {new Date(selectedCustomer.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>

            {selectedCustomer.hostingAccounts && selectedCustomer.hostingAccounts.length > 0 && (
              <div className="mt-8">
                <h3 className="text-lg font-bold mb-4">Hosting Accounts</h3>
                <div className="space-y-4">
                  {selectedCustomer.hostingAccounts.map((account) => (
                    <div key={account.id} className="border rounded p-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-gray-600 text-sm">Account Name</label>
                          <div>{account.accountName}</div>
                        </div>
                        <div>
                          <label className="text-gray-600 text-sm">Status</label>
                          <div
                            className={
                              account.status === 'active'
                                ? 'text-green-600'
                                : 'text-red-600'
                            }
                          >
                            {account.status?.toUpperCase()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">Name</th>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">Email</th>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">Status</th>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">
                    Member Since
                  </th>
                  <th className="px-6 py-4 text-left text-gray-700 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer) => (
                  <tr key={customer.id} className="border-t border-gray-200">
                    <td className="px-6 py-4">
                      {customer.firstName} {customer.lastName}
                    </td>
                    <td className="px-6 py-4">{customer.email}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded text-sm font-semibold ${
                          customer.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {customer.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {new Date(customer.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => fetchCustomerDetails(customer.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {customers.length === 0 && (
              <div className="text-center py-8 text-gray-600">No customers found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
